import { useEffect, useMemo, useState } from 'react';
import { useLeavePolicyStore, LeavePolicy } from '../../../store/leavePolicyStore';
import { useEmployeeStore } from '../../../store/employeeStore';
import { Card, CardContent, CardHeader } from '../../../components/Card';
import { Button } from '../../../components/Button';
import { Input } from '../../../components/Input';
import { Badge } from '../../../components/Badge';
import { Loader2, Plus, Trash2, Pencil, Users2, PlayCircle, X, ScrollText, Scale } from 'lucide-react';
import toast from 'react-hot-toast';

const YEAR = new Date().getFullYear();

/** A set of checkbox chips for a multi-select rule dimension. */
function ChipMulti({ label, all, selected, onToggle }: { label: string; all: string[]; selected: string[]; onToggle: (v: string) => void }) {
  return (
    <div>
      <p className="text-xs font-semibold text-muted-foreground mb-1.5">{label} <span className="font-normal">(empty = any)</span></p>
      <div className="flex flex-wrap gap-1.5">
        {all.length === 0 && <span className="text-xs text-muted-foreground">No values</span>}
        {all.map((v) => {
          const on = selected.includes(v);
          return (
            <button key={v} type="button" onClick={() => onToggle(v)}
              className={`rounded-full border px-2.5 py-1 text-xs font-medium ${on ? 'border-primary/40 bg-primary/10 text-primary' : 'border-border bg-card text-muted-foreground hover:text-foreground'}`}>
              {v}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function PolicyEditor({ initial, onClose }: { initial: Partial<LeavePolicy> | null; onClose: () => void }) {
  const { options, createPolicy, updatePolicy, getPolicy } = useLeavePolicyStore();
  const [form, setForm] = useState<any>({
    name: '', description: '', priority: 0, isActive: true,
    designations: [], departments: [], employmentTypes: [],
    minExperienceYears: '', maxExperienceYears: '',
    allocations: {} as Record<string, number>,
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      if (initial?.id) {
        const full = await getPolicy(initial.id);
        const alloc: Record<string, number> = {};
        (full.allocations || []).forEach((a) => { alloc[a.leave_type_id] = Number(a.days); });
        setForm({
          name: full.name, description: full.description || '', priority: full.priority, isActive: full.is_active,
          designations: full.designations || [], departments: full.departments || [], employmentTypes: full.employment_types || [],
          minExperienceYears: full.min_experience_years ?? '', maxExperienceYears: full.max_experience_years ?? '',
          allocations: alloc,
        });
      }
    })();
  }, [initial?.id, getPolicy]);

  const toggle = (key: string, v: string) =>
    setForm((f: any) => ({ ...f, [key]: f[key].includes(v) ? f[key].filter((x: string) => x !== v) : [...f[key], v] }));

  const save = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    const allocations = Object.entries(form.allocations)
      .filter(([, d]) => Number(d) > 0)
      .map(([leaveTypeId, days]) => ({ leaveTypeId, days: Number(days) }));
    const payload = {
      name: form.name.trim(), description: form.description, priority: Number(form.priority) || 0, isActive: form.isActive,
      designations: form.designations, departments: form.departments, employmentTypes: form.employmentTypes,
      minExperienceYears: form.minExperienceYears === '' ? null : Number(form.minExperienceYears),
      maxExperienceYears: form.maxExperienceYears === '' ? null : Number(form.maxExperienceYears),
      allocations,
    };
    setSaving(true);
    try {
      if (initial?.id) { await updatePolicy(initial.id, payload); toast.success('Policy updated'); }
      else { await createPolicy(payload); toast.success('Policy created'); }
      onClose();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Could not save policy');
    } finally { setSaving(false); }
  };

  return (
    <Card className="border-primary/30">
      <CardHeader className="flex flex-row items-center justify-between px-6 py-4">
        <h3 className="font-display text-section-title text-foreground">{initial?.id ? 'Edit policy' : 'New policy'}</h3>
        <Button variant="ghost" size="sm" onClick={onClose}><X className="size-4" /></Button>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="md:col-span-2">
            <p className="text-xs font-semibold text-muted-foreground mb-1.5">Name</p>
            <Input value={form.name} onChange={(e) => setForm((f: any) => ({ ...f, name: e.target.value }))} placeholder="e.g. Senior Engineers (3+ yrs)" />
          </div>
          <div>
            <p className="text-xs font-semibold text-muted-foreground mb-1.5">Priority <span className="font-normal">(higher wins)</span></p>
            <Input type="number" value={form.priority} onChange={(e) => setForm((f: any) => ({ ...f, priority: e.target.value }))} />
          </div>
        </div>

        <div>
          <p className="text-xs font-semibold text-muted-foreground mb-2">Eligibility rules</p>
          <div className="space-y-3 rounded-lg border border-border p-4">
            <ChipMulti label="Designations" all={options.designations} selected={form.designations} onToggle={(v) => toggle('designations', v)} />
            <ChipMulti label="Departments" all={options.departments} selected={form.departments} onToggle={(v) => toggle('departments', v)} />
            <ChipMulti label="Employment types" all={options.employmentTypes} selected={form.employmentTypes} onToggle={(v) => toggle('employmentTypes', v)} />
            <div className="flex items-end gap-3">
              <div>
                <p className="text-xs font-semibold text-muted-foreground mb-1.5">Min experience (yrs)</p>
                <Input type="number" className="w-28" value={form.minExperienceYears} onChange={(e) => setForm((f: any) => ({ ...f, minExperienceYears: e.target.value }))} placeholder="any" />
              </div>
              <div>
                <p className="text-xs font-semibold text-muted-foreground mb-1.5">Max experience (yrs)</p>
                <Input type="number" className="w-28" value={form.maxExperienceYears} onChange={(e) => setForm((f: any) => ({ ...f, maxExperienceYears: e.target.value }))} placeholder="any" />
              </div>
            </div>
          </div>
        </div>

        <div>
          <p className="text-xs font-semibold text-muted-foreground mb-2">Leave allocations <span className="font-normal">(days per year; 0 = not granted)</span></p>
          <div className="space-y-2 rounded-lg border border-border p-4">
            {options.leaveTypes.map((lt) => (
              <div key={lt.id} className="flex items-center justify-between">
                <span className="text-sm text-foreground">{lt.name}</span>
                <Input type="number" className="w-24"
                  value={form.allocations[lt.id] ?? 0}
                  onChange={(e) => setForm((f: any) => ({ ...f, allocations: { ...f.allocations, [lt.id]: Number(e.target.value) } }))} />
              </div>
            ))}
          </div>
        </div>

        <div className="flex items-center justify-between">
          <label className="flex items-center gap-2 text-sm text-muted-foreground">
            <input type="checkbox" className="accent-primary" checked={form.isActive} onChange={(e) => setForm((f: any) => ({ ...f, isActive: e.target.checked }))} />
            Active
          </label>
          <Button variant="primary" disabled={saving} onClick={save}>
            {saving ? <Loader2 className="size-4 animate-spin" /> : (initial?.id ? 'Save changes' : 'Create policy')}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function PoliciesTab() {
  const { policies, loadPolicies, loadOptions, deletePolicy, getMatches, applyPolicy, loading } = useLeavePolicyStore();
  const [editing, setEditing] = useState<Partial<LeavePolicy> | null | 'new'>(null);
  const [matchFor, setMatchFor] = useState<{ id: string; names: string[] } | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  useEffect(() => { loadPolicies(); loadOptions(); }, [loadPolicies, loadOptions]);

  const ruleSummary = (p: LeavePolicy) => {
    const parts: string[] = [];
    if (p.designations?.length) parts.push(p.designations.join('/'));
    if (p.departments?.length) parts.push(p.departments.join('/'));
    if (p.employment_types?.length) parts.push(p.employment_types.join('/'));
    if (p.min_experience_years != null) parts.push(`≥${p.min_experience_years}y`);
    if (p.max_experience_years != null) parts.push(`≤${p.max_experience_years}y`);
    return parts.length ? parts.join(' · ') : 'Everyone';
  };

  const preview = async (id: string) => {
    try {
      const rows = await getMatches(id);
      setMatchFor({ id, names: rows.map((r: any) => `${r.first_name} ${r.last_name}`.trim()) });
    } catch { toast.error('Could not load matches'); }
  };

  const apply = async (p: LeavePolicy) => {
    if (!window.confirm(`Apply "${p.name}" for ${YEAR}? This sets leave balances for matching & assigned employees (days already taken are kept).`)) return;
    setBusyId(p.id);
    try {
      const r = await applyPolicy(p.id, YEAR);
      toast.success(`Applied to ${r.employees} employee(s) — ${r.balancesWritten} balances updated`);
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Could not apply policy');
    } finally { setBusyId(null); }
  };

  const remove = async (p: LeavePolicy) => {
    if (!window.confirm(`Delete "${p.name}"? Existing balances stay; the policy and its assignments are removed.`)) return;
    try { await deletePolicy(p.id); toast.success('Policy deleted'); } catch { toast.error('Could not delete'); }
  };

  if (editing) {
    return <PolicyEditor initial={editing === 'new' ? null : editing} onClose={() => { setEditing(null); loadPolicies(); }} />;
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button variant="primary" size="sm" onClick={() => setEditing('new')}><Plus className="size-4" /> New policy</Button>
      </div>
      {loading ? (
        <div className="flex items-center gap-2 py-8 text-sm text-muted-foreground"><Loader2 className="size-4 animate-spin" /> Loading policies…</div>
      ) : policies.length === 0 ? (
        <Card><CardContent className="p-10 text-center text-muted-foreground">
          <ScrollText className="mx-auto size-8 mb-2 opacity-50" />
          No leave policies yet. Create one to allocate quotas by designation, department or experience.
        </CardContent></Card>
      ) : (
        <div className="space-y-3">
          {policies.map((p) => (
            <Card key={p.id}>
              <CardContent className="p-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-semibold text-foreground">{p.name}</p>
                      <Badge variant="default">priority {p.priority}</Badge>
                      {!p.is_active && <Badge variant="secondary">inactive</Badge>}
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5">Eligibility: {ruleSummary(p)}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {(p.allocations && p.allocations.length)
                        ? p.allocations.map((a) => `${a.leave_type_name} ${a.days}`).join(' · ')
                        : <span className="text-warning-strong">No allocations yet</span>}
                    </p>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="sm" onClick={() => preview(p.id)} title="Preview matches"><Users2 className="size-4" /></Button>
                    <Button variant="ghost" size="sm" disabled={busyId === p.id} onClick={() => apply(p)} title="Apply">
                      {busyId === p.id ? <Loader2 className="size-4 animate-spin" /> : <PlayCircle className="size-4 text-primary" />}
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => setEditing(p)} title="Edit"><Pencil className="size-4" /></Button>
                    <Button variant="ghost" size="sm" onClick={() => remove(p)} title="Delete"><Trash2 className="size-4 text-destructive" /></Button>
                  </div>
                </div>
                {matchFor?.id === p.id && (
                  <div className="mt-3 rounded-lg border border-border bg-background/50 p-3">
                    <p className="text-xs font-semibold text-muted-foreground mb-1">{matchFor.names.length} matching employee(s)</p>
                    <p className="text-xs text-foreground">{matchFor.names.join(', ') || 'None'}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function BalancesTab() {
  const { employees, fetchEmployees } = useEmployeeStore();
  const { getEmployeeBalances, setBalance } = useLeavePolicyStore();
  const [employeeId, setEmployeeId] = useState('');
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [savingType, setSavingType] = useState<string | null>(null);

  useEffect(() => { fetchEmployees({ limit: 1000 }); }, [fetchEmployees]);

  const load = async (id: string) => {
    if (!id) { setRows([]); return; }
    setLoading(true);
    try { setRows(await getEmployeeBalances(id, YEAR)); }
    catch { toast.error('Could not load balances'); }
    finally { setLoading(false); }
  };

  const save = async (leaveTypeId: string, allocated: number) => {
    setSavingType(leaveTypeId);
    try {
      const updated = await setBalance(employeeId, leaveTypeId, allocated, YEAR);
      setRows((prev) => prev.map((r) => (r.leave_type_id === leaveTypeId
        ? { ...r, allocated: updated.allocated, remaining: updated.remaining } : r)));
      toast.success('Balance updated');
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Could not update');
    } finally { setSavingType(null); }
  };

  const sorted = useMemo(() => [...employees].sort((a, b) =>
    `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`)), [employees]);

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <span className="text-sm text-muted-foreground">Employee</span>
        <select value={employeeId} onChange={(e) => { setEmployeeId(e.target.value); load(e.target.value); }}
          className="rounded-lg border border-border bg-card px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30">
          <option value="">Select an employee…</option>
          {sorted.map((e) => <option key={e.id} value={e.id}>{e.firstName} {e.lastName}{e.designation ? ` · ${e.designation}` : ''}</option>)}
        </select>
        <span className="text-xs text-muted-foreground">Year {YEAR}</span>
      </div>

      {!employeeId ? (
        <Card><CardContent className="p-10 text-center text-muted-foreground">Pick an employee to view and adjust their leave balances.</CardContent></Card>
      ) : loading ? (
        <div className="flex items-center gap-2 py-6 text-sm text-muted-foreground"><Loader2 className="size-4 animate-spin" /> Loading…</div>
      ) : (
        <Card><CardContent className="p-0">
          <table className="w-full text-left text-sm">
            <thead><tr className="border-b border-border">
              <th className="px-4 py-3 font-semibold text-muted-foreground">Leave type</th>
              <th className="px-4 py-3 font-semibold text-muted-foreground">Allocated</th>
              <th className="px-4 py-3 font-semibold text-muted-foreground">Taken</th>
              <th className="px-4 py-3 font-semibold text-muted-foreground">Remaining</th>
              <th className="px-4 py-3"></th>
            </tr></thead>
            <tbody className="divide-y divide-border-12">
              {rows.map((r) => (
                <BalanceRow key={r.leave_type_id} row={r} saving={savingType === r.leave_type_id} onSave={save} />
              ))}
            </tbody>
          </table>
        </CardContent></Card>
      )}
    </div>
  );
}

function BalanceRow({ row, saving, onSave }: { row: any; saving: boolean; onSave: (id: string, v: number) => void }) {
  const [val, setVal] = useState<number>(Number(row.allocated));
  useEffect(() => { setVal(Number(row.allocated)); }, [row.allocated]);
  const dirty = Number(val) !== Number(row.allocated);
  return (
    <tr className="hover:bg-card/40">
      <td className="px-4 py-2.5 font-medium text-foreground">{row.type}</td>
      <td className="px-4 py-2.5"><Input type="number" className="w-24" value={val} onChange={(e) => setVal(Number(e.target.value))} /></td>
      <td className="px-4 py-2.5 text-muted-foreground">{Number(row.taken)}</td>
      <td className="px-4 py-2.5 text-muted-foreground">{Number(row.remaining)}</td>
      <td className="px-4 py-2.5 text-right">
        <Button variant="primary" size="sm" disabled={!dirty || saving} onClick={() => onSave(row.leave_type_id, val)}>
          {saving ? <Loader2 className="size-4 animate-spin" /> : 'Save'}
        </Button>
      </td>
    </tr>
  );
}

export function LeavePolicies() {
  const [tab, setTab] = useState<'policies' | 'balances'>('policies');
  return (
    <div className="space-y-6">
      <div>
        <h1 className="tracking-tight text-foreground font-display text-page-title">Leave Policies & Balances</h1>
        <p className="text-muted-foreground mt-1">Allocate leave quotas by rules (designation, department, experience), apply them to employees, and adjust individual balances.</p>
      </div>
      <div className="flex gap-2 border-b border-border">
        {[{ id: 'policies', label: 'Policies', icon: ScrollText }, { id: 'balances', label: 'Employee balances', icon: Scale }].map((t) => {
          const Icon = t.icon;
          return (
            <button key={t.id} onClick={() => setTab(t.id as any)}
              className={`flex items-center gap-1.5 pb-3 px-3 text-sm font-semibold ${tab === t.id ? 'text-primary border-b-2 border-primary -mb-px' : 'text-muted-foreground hover:text-foreground'}`}>
              <Icon className="size-4" /> {t.label}
            </button>
          );
        })}
      </div>
      {tab === 'policies' ? <PoliciesTab /> : <BalancesTab />}
    </div>
  );
}
